import { useState } from "react";
import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPin() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [newPin, setNewPin] = useState("");
  const [newPin2, setNewPin2] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  if (!loading && user && !user.is_super_admin) return <Navigate to="/dashboard" replace />;

  const submit = async () => {
    if (newPin.length < 8) { toast.error("PIN must be at least 8 characters."); return; }
    if (newPin !== newPin2) { toast.error("PINs do not match."); return; }
    setSaving(true);
    try {
      await api.post("/admin/security/pin/reset", { token, new_pin: newPin });
      setDone(true);
      toast.success("Delete PIN reset successfully");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset PIN");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="px-6 py-16 max-w-md w-full mx-auto">
        <div className="border border-white/10 rounded-xl p-6 bg-zinc-900/40">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={20} className="text-amber-400" />
            <h1 className="font-heading text-xl font-bold tracking-tight">Reset delete PIN</h1>
          </div>

          {!token ? (
            <p data-testid="reset-pin-no-token" className="text-sm text-red-400 mt-3">
              Missing or invalid reset link. Open the "Forgot your PIN?" option in Admin to email yourself a fresh link.
            </p>
          ) : done ? (
            <div data-testid="reset-pin-success" className="mt-4">
              <div className="flex items-center gap-2 text-emerald-400 text-sm">
                <CheckCircle size={18} weight="fill" /> Your delete PIN has been updated.
              </div>
              <Button
                data-testid="reset-pin-go-admin"
                onClick={() => navigate("/admin")}
                className="mt-5 bg-blue-600 hover:bg-blue-500"
              >
                Back to Admin
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-400 mt-2 mb-4">Set a new delete PIN (minimum 8 characters). This link expires 15 minutes after it was sent.</p>
              <div className="space-y-3">
                <Input
                  data-testid="reset-pin-new"
                  type="password"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  placeholder="New PIN (min 8 characters)"
                  className="bg-black border-zinc-800 focus-visible:ring-blue-600"
                />
                <Input
                  data-testid="reset-pin-confirm"
                  type="password"
                  value={newPin2}
                  onChange={(e) => setNewPin2(e.target.value)}
                  placeholder="Confirm new PIN"
                  className="bg-black border-zinc-800 focus-visible:ring-blue-600"
                />
                <Button
                  data-testid="reset-pin-submit"
                  onClick={submit}
                  disabled={saving || !newPin || !newPin2}
                  className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40"
                >
                  {saving ? "Saving…" : "Set new PIN"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
